import { Suspense, useContext, useState } from "react";
import { motion } from "motion/react";
import { useProductTranslations } from "../../hooks/TranslationHooks";
import BjelinButton from "../BjelinButton";
import CompactPackageCalculator from "../PackageCalculator/compact";
import { CartContext } from "../../functional-components/CartContext";
import { productIsPurchasable, StatusCodes } from "../../utils/ProductUtils";
import QuantityInput from "../QuantityInput";
import { addVat } from "../../utils/VatUtils";
import { useUser } from "../../functional-components/UserContext";
import Icon from "../Icon";
import useShowPricesWithVat from "../../hooks/useShowPricesWithVat";
import { useProductPrice, useUserProduct } from "../../hooks/ProductHooks";
import { useMediaQuery } from "react-responsive";
import "./style.scss";
import FormattedPrice from "../FormattedPrice";
import ProductStock from "../ProductStock";
import { multiply } from "lodash";
import { useSiteFeatures } from "../../hooks/useSiteFeatures";

const ProductPurchaseAreaInner = ({ sku }) => {
  const isSmall = useMediaQuery({ maxWidth: 991.98 });
  const isVerySmall = useMediaQuery({ maxWidth: 699.98 });
  const { b2b, isBusiness } = useUser();
  const features = useSiteFeatures();
  const tp = useProductTranslations();
  const { loggedAddToCart, loggedAddSampleToCart } = useContext(CartContext);
  const product = useUserProduct(sku);
  const showPricesWithVat = useShowPricesWithVat();
  const [amount, setAmount] = useState(1);
  const [calculatorExpanded, setCalculatorExpanded] = useState(false);

  const { name, saleable, sampleSaleable, replaced_by } = product;
  const {
    basePrice,
    unitPrice,
    priceUnit,
    isPerSquareMeter,
    currency,
    unitQuantityFactor
  } = useProductPrice(product, b2b);

  if (!product) return null;

  const addToCartHandler = e => {
    loggedAddToCart(product, amount, false, b2b);
    e.stopPropagation(true);
    setCalculatorExpanded(false);
  };
  const addSampleToCartHandler = e => {
    loggedAddSampleToCart(product);
    e.stopPropagation(true);
  };
  const isAvailable = productIsPurchasable(product, b2b);
  const { upcoming, unavailable, discontinued, stock, hasSampleInStock } =
    product;
  const replacedBy = replaced_by || [];

  const pricePerPackage = showPricesWithVat ? addVat(basePrice) : basePrice;
  const pricePerUnit = showPricesWithVat ? addVat(unitPrice) : unitPrice;
  const total = multiply(amount, pricePerPackage);

  const stockStatusText =
    unavailable ||
    (discontinued && stock.status === StatusCodes.soldOut && !b2b)
      ? tp("shipping_status.unavailable")
      : stock.status === StatusCodes.soldOut && !b2b
      ? upcoming
        ? tp("shipping_status.upcoming")
        : tp("shipping_status.sold_out")
      : stock.status === StatusCodes.outOfStock && !b2b
      ? tp("shipping_status.out_of_stock")
      : tp("add_to_cart");
  return (
    <>
      <div className={"product-purchase-area"}>
        <div className={"product-purchase-area-container"}>
          <div className={"product-purchase-area-inner"}>
            {((features.shop() && saleable) || replacedBy.length === 0) && (
              <>
                <div>
                  {isAvailable || !isSmall ? (
                    <div className={"amount-selector"}>
                      {isPerSquareMeter ? (
                        <>
                          {isVerySmall ? (
                            <div className="package-calculator-expandable">
                              <button
                                className="amount-expand-btn"
                                onClick={() => {
                                  setCalculatorExpanded(expanded => !expanded);
                                }}
                              >
                                {amount} {tp("sales_unit.per_square_meter")}{" "}
                                <FormattedPrice
                                  price={total}
                                  currency={currency}
                                  price_class_name="price"
                                />
                                <Icon
                                  name={
                                    calculatorExpanded
                                      ? "chevron-down"
                                      : "chevron-up"
                                  }
                                />
                              </button>
                              <div className="stock-status-cell">
                                <ProductStock
                                  product={product}
                                  b2b={b2b}
                                  short
                                />
                              </div>
                            </div>
                          ) : (
                            <CompactPackageCalculator
                              product={product}
                              m2PerPackage={unitQuantityFactor}
                              pricePerPackage={pricePerPackage}
                              pricePerM2={pricePerUnit}
                              priceUnit={priceUnit}
                              stock={stock.availableStock}
                              valuePkg={1}
                              valueCallback={setAmount}
                              currency={currency}
                              b2b={b2b}
                              isBusiness={isBusiness}
                              short={isSmall}
                            />
                          )}
                        </>
                      ) : (
                        <QuantityInput
                          price={
                            showPricesWithVat ? addVat(basePrice) : basePrice
                          }
                          salesUnit={priceUnit}
                          stock={stock.availableStock}
                          valueCallback={setAmount}
                          product={product}
                          currency={currency}
                          b2b={b2b}
                          isBusiness={isBusiness}
                          short={isSmall}
                        />
                      )}
                    </div>
                  ) : (
                    /* mobile and not available and NOT if purchase sample btn is showing */
                    (!isVerySmall || (saleable && !sampleSaleable)) && (
                      <div className="ms-3 center-content">
                        <div>
                          <FormattedPrice
                            price={
                              priceUnit === "per_square_meter"
                                ? pricePerUnit
                                : pricePerPackage
                            }
                            price_unit={priceUnit}
                            currency={currency}
                          />
                        </div>
                        <div className="stock-status-cell d-block">
                          <ProductStock
                            product={product}
                            b2b={b2b}
                            short={isVerySmall}
                            salesUnit={priceUnit}
                          />
                        </div>
                      </div>
                    )
                  )}
                </div>

                {saleable && (
                  <div
                    style={{
                      marginLeft: "auto",
                      display: "flex",
                      columnGap: "0.5em"
                    }}
                  >
                    <BjelinButton
                      className={"btn-rounded btn-purchase"}
                      disabled={!isAvailable}
                      color={
                        stock.status === StatusCodes.backOrder
                          ? "bjelin-brown"
                          : "primary"
                      }
                      block={true}
                      icon={isAvailable ? "cart-plus" : ""}
                      icon_position={"before"}
                      title={
                        unavailable ||
                        (discontinued &&
                          stock.status === StatusCodes.soldOut &&
                          !b2b)
                          ? tp("shipping_status.unavailable")
                          : stock.status === StatusCodes.soldOut && !b2b
                          ? upcoming
                            ? tp("shipping_status.upcoming")
                            : tp("shipping_status.sold_out")
                          : stock.status === StatusCodes.outOfStock && !b2b
                          ? tp("shipping_status.out_of_stock")
                          : tp("add_to_cart")
                      }
                      onClick={addToCartHandler}
                    >
                      {(!isAvailable || !isSmall) && stockStatusText}
                    </BjelinButton>

                    {sampleSaleable &&
                      (hasSampleInStock ? (
                        <button
                          className={"sample-btn"}
                          onClick={addSampleToCartHandler}
                          disabled={!hasSampleInStock}
                        >
                          {tp("order_sample")}
                        </button>
                      ) : (
                        <button disabled className={"sample-btn disabled"}>
                          {tp("sample_out_of_stock")}
                        </button>
                      ))}
                  </div>
                )}
              </>
            )}

            {product.statusText && (
              <div className={"status-text"} title={product.statusText}>
                <Icon name="triangle-exclamation" />
              </div>
            )}
          </div>
        </div>
      </div>
      {calculatorExpanded && (
        <>
          <motion.div
            className="package-calculator-overlay-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1, delay: 0.1 }}
            onClick={() => setCalculatorExpanded(false)}
          />
          <motion.div
            role="dialog"
            aria-modal
            className="package-calculator-expanded"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "2em"
              }}
            >
              <h3>Köp {name}</h3>
            </div>
            <CompactPackageCalculator
              product={product}
              m2PerPackage={unitQuantityFactor}
              pricePerPackage={pricePerPackage}
              pricePerM2={showPricesWithVat ? addVat(unitPrice) : unitPrice}
              priceUnit={priceUnit}
              stock={stock.availableStock}
              valuePkg={1}
              valueCallback={setAmount}
              currency={currency}
              b2b={b2b}
              isBusiness={isBusiness}
            />

            {/*For future project:
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "2em"
              }}
            >
              <BjelinButton
                className={"btn-rounded btn-calc"}
                disabled={!isAvailable}
                color={"secondary"}
                icon_position={"before"}
              >
                Räkna ut hur mycket du behöver
              </BjelinButton>
            </div>*/}

            {saleable && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "2em"
                }}
              >
                <BjelinButton
                  className={"btn-rounded btn-purchase"}
                  disabled={!isAvailable}
                  color={
                    stock.status === StatusCodes.backOrder
                      ? "bjelin-brown"
                      : "primary"
                  }
                  block={true}
                  icon={isAvailable ? "cart-plus" : ""}
                  icon_position={"before"}
                  title={
                    unavailable ||
                    (discontinued &&
                      stock.status === StatusCodes.soldOut &&
                      !b2b)
                      ? tp("shipping_status.unavailable")
                      : stock.status === StatusCodes.soldOut && !b2b
                      ? upcoming
                        ? tp("shipping_status.upcoming")
                        : tp("shipping_status.sold_out")
                      : stock.status === StatusCodes.outOfStock && !b2b
                      ? tp("shipping_status.out_of_stock")
                      : tp("add_to_cart")
                  }
                  onClick={addToCartHandler}
                >
                  {unavailable ||
                  (discontinued && stock.status === StatusCodes.soldOut && !b2b)
                    ? tp("shipping_status.unavailable")
                    : stock.status === StatusCodes.soldOut && !b2b
                    ? upcoming
                      ? tp("shipping_status.upcoming")
                      : tp("shipping_status.sold_out")
                    : stock.status === StatusCodes.outOfStock && !b2b
                    ? tp("shipping_status.out_of_stock")
                    : tp("add_to_cart")}
                </BjelinButton>
              </div>
            )}
          </motion.div>
        </>
      )}
    </>
  );
};

const FloatingProductPurchaseArea = ({ sku, children }) => {
  return sku ? (
    <Suspense fallback={null}>
      <ProductPurchaseAreaInner sku={sku}>{children}</ProductPurchaseAreaInner>
    </Suspense>
  ) : null;
};

export default FloatingProductPurchaseArea;
